import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  businessRelationshipApi,
  type TradeParams,
} from '@/entities/business-relationship/api/businessRelationship.api';

/** 미거래 의약품 한 페이지 크기 — 앱(defaultPageSize)과 같게 둔다 */
const UNTRADED_PAGE_SIZE = 20;

/** 처방 등록 후 목록 · 상세 · 등록 현황을 한 번에 invalidate할 수 있게 all을 공통 접두사로 둔다 */
export const businessRelationshipKeys = {
  all: ['business-relationships'] as const,
  list: () => [...businessRelationshipKeys.all, 'list'] as const,
  detail: (relationshipId: number) =>
    [...businessRelationshipKeys.all, 'detail', relationshipId] as const,
  trades: (relationshipId: number, params: TradeParams) =>
    [
      ...businessRelationshipKeys.all,
      'trades',
      relationshipId,
      params,
    ] as const,
  registrations: (relationshipId: number, year: number) =>
    [
      ...businessRelationshipKeys.all,
      'registrations',
      relationshipId,
      year,
    ] as const,
  untradedDrugs: (relationshipId: number, canApply: boolean) =>
    [
      ...businessRelationshipKeys.all,
      'untraded-drugs',
      relationshipId,
      canApply,
    ] as const,
};

export const useBusinessRelationshipList = () =>
  useQuery({
    queryKey: businessRelationshipKeys.list(),
    queryFn: businessRelationshipApi.list,
  });

export const useBusinessRelationshipDetail = (relationshipId?: number) =>
  useQuery({
    queryKey: businessRelationshipKeys.detail(relationshipId ?? 0),
    queryFn: () => businessRelationshipApi.detail(relationshipId!),
    enabled: !!relationshipId,
  });

/** M-19 · M-20 드로어 — 검색 · 정렬은 서버. 입력 중에는 이전 결과를 유지한다 */
export const useTrades = (
  relationshipId: number,
  params: TradeParams,
  enabled = true,
) =>
  useQuery({
    queryKey: businessRelationshipKeys.trades(relationshipId, params),
    queryFn: () => businessRelationshipApi.trades(relationshipId, params),
    placeholderData: keepPreviousData,
    enabled,
  });

/** 연도별 처방 등록 현황 — 이미지가 있는 (등록월, 정산월)만 온다 */
export const usePrescriptionRegistrations = (
  relationshipId: number,
  registrationYear: number,
) =>
  useQuery({
    queryKey: businessRelationshipKeys.registrations(
      relationshipId,
      registrationYear,
    ),
    queryFn: () =>
      businessRelationshipApi.prescriptionRegistrations(
        relationshipId,
        registrationYear,
      ),
    placeholderData: keepPreviousData,
  });

/**
 * M-22 놓치고 있는 의약품 — 신청 가능 · 불가를 canApply로 따로 불러 각자 페이징한다.
 * 기준 처방월은 생략해 서버 최신 처방월(상세 standardYearMonth와 같음)을 쓴다.
 */
export const useUntradedDrugs = (
  relationshipId: number,
  canApply: boolean,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: businessRelationshipKeys.untradedDrugs(relationshipId, canApply),
    queryFn: ({ pageParam }) =>
      businessRelationshipApi.untradedDrugs(relationshipId, {
        canApply,
        page: pageParam,
        size: UNTRADED_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.isLast ? undefined : last.pageNo + 1),
    enabled,
  });
